import {
  ABIActionDrawer,
  ABIAppButton,
  ABIInlineLoader,
  ABISearchBar,
  DrawerContainerPosition,
  ExpandMoreIcon
} from '@ab-inbev-labs/ux-react-components';
import {
  UserProfileQueryKeys,
  findBrewery,
  loadStaticBreweriesList,
  selectBreweryListAsSearchBar,
  useBreweryListQueryWithFallback,
  useGeneralSettingsGetQuery,
  useGeneralSettingsSaveMutation
} from '@ab-inbev/sam-api/clients/user-profile-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './UserProfile.scss';
import { AdditionalAppBarSettingsContent, LanguageOptions, UnitsOptions } from './userProfileSettings/types';
import { UserSettingButtons } from './userProfileSettings/UserSettingButtons';
import { LanguageSettings, UnitsSettings } from './userProfileSettings/constants';
import { UserProfileModal } from './UserProfileModal';
import { UserInfoDisplay } from './UserInfoDisplay';
import { PermissionsDisplay } from './userProfileSettings/PermissionsDisplay';
import { LayoutTestIds } from '@ab-inbev/sam-utils';

export type UserProfileProps = {
  additionalSettings?: AdditionalAppBarSettingsContent;
};

export const UserProfile: FC<UserProfileProps> = ({ additionalSettings }) => {
  const { t, i18n } = useTranslation();

  // API query definitions.
  const queryClient = useQueryClient();
  const { data: loadedSettingsData, isLoading, isError } = useGeneralSettingsGetQuery();
  const { mutateAsync } = useGeneralSettingsSaveMutation();
  const { data: ApiBreweryOptions } = useBreweryListQueryWithFallback(
    loadStaticBreweriesList(),
    selectBreweryListAsSearchBar
  );

  const [isUserProfileActionDrawerOpen, setIsUserProfileActionDrawerOpen] = useState(false);

  // Settings state variables
  const [refreshSettings, setRefreshSettings] = useState(true);
  const [languageSetting, setLanguageSetting] = useState<string>(LanguageOptions.English);
  const [brewerySetting, setBrewerySetting] = useState<string>('');
  const [unitsSetting, setUnitsSetting] = useState<string>(UnitsOptions.Imperial);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);
  const [showPermissionsInfo, setShowPermissionsInfo] = useState(false);

  //#region User Profile API Calls

  // GET settings
  useEffect(() => {
    if (refreshSettings && loadedSettingsData) {
      setRefreshSettings(false);

      if (loadedSettingsData.breweryCode !== brewerySetting) {
        setBrewerySetting(loadedSettingsData.breweryCode ?? '');
      }

      if (loadedSettingsData.language !== languageSetting) {
        setLanguageSetting(loadedSettingsData.language ?? LanguageOptions.English);
      }

      if (loadedSettingsData.units !== unitsSetting) {
        setUnitsSetting(loadedSettingsData.units ?? '');
      }
    }
  }, [refreshSettings, loadedSettingsData, brewerySetting, languageSetting, unitsSetting, i18n]);

  const invalidateSettingsQuery = async () =>
    await queryClient.invalidateQueries({
      queryKey: [UserProfileQueryKeys.UseGeneralSettingsGet]
    });

  // PUT settings updates.
  const updateUserProfileSettings = async ({
    language,
    breweryCode,
    units
  }: {
    language: string;
    breweryCode: string;
    units: string;
  }) => {
    await mutateAsync(
      {
        language,
        breweryCode,
        units
      },
      {
        onSuccess: async () => {
          await invalidateSettingsQuery();
          setRefreshSettings(true);
        }
      }
    );
  };

  // #endregion

  // #region User Profile Action Drawer Button
  const userProfileActionDrawerButton = (
    <>
      <UserInfoDisplay appName={additionalSettings?.appName} permissionOrder={additionalSettings?.permissionOrder} />
      <ExpandMoreIcon
        className={`user-profile-action-drawer-button-dropdown-icon${isUserProfileActionDrawerOpen ? '-open ' : ' '}`}
      />
    </>
  );
  // #endregion

  // #region Brewery Selection Accordion
  const hasModalContent = !!additionalSettings?.listOfPermissions || !!additionalSettings?.additionalUserProfileContent;

  const brewerySelectionAccordion = (
    <div className={`user-profile-line-container ${hasModalContent ? 'show-border-top' : ''}`}>
      <div className="user-profile-left">{i18n.t('common.breweryTerms.brewery')}</div>
      <ABISearchBar
        placeholder={t('userProfile.brewerySelectionSearchPlaceholder')}
        className="user-profile-right"
        isBorderLight
        selectedValue={brewerySetting !== '' ? findBrewery({ breweryCode: brewerySetting }) : undefined}
        suggestions={ApiBreweryOptions}
        allowSuggestionsOnly
        onChange={(event) => {
          if (event.includes('(') && event.includes(')')) {
            const selectedBrewCode = event.substring(event.lastIndexOf('(') + 1, event.lastIndexOf(')')).trim();
            updateUserProfileSettings({
              language: languageSetting,
              breweryCode: selectedBrewCode,
              units: unitsSetting
            });
          } else if (event === '') {
            updateUserProfileSettings({
              language: languageSetting,
              breweryCode: '',
              units: unitsSetting
            });
          }
        }}
        data-testid={LayoutTestIds.BrewerySelectionSearchBar}
      />
    </div>
  );
  // #endregion

  // #region Language and Units Accordion

  const languageAccordion = (
    <div className={`user-profile-setting-accordion `}>
      <UserSettingButtons
        title={t('userProfile.languageLabel')}
        settingButtonData={LanguageSettings}
        currentSetting={languageSetting}
        updateCurrentSetting={(selectedOption: string) => {
          setLanguageSetting(selectedOption);
          i18n.changeLanguage(selectedOption);
          updateUserProfileSettings({
            language: selectedOption,
            breweryCode: brewerySetting,
            units: unitsSetting
          });
        }}
      />
    </div>
  );

  const unitsAccordion = (
    <div className={'user-profile-setting-accordion'}>
      <UserSettingButtons
        title={t('userProfile.unitsLabel')}
        settingButtonData={UnitsSettings}
        currentSetting={unitsSetting}
        updateCurrentSetting={(selectedOption: string) => {
          setUnitsSetting(selectedOption);
          updateUserProfileSettings({
            language: languageSetting,
            breweryCode: brewerySetting,
            units: selectedOption
          });
        }}
      />
    </div>
  );

  const permissionsButton = (
    <div>
      <button
        onClick={() => setShowPermissionsInfo(true)}
        className="user-profile-modal-button"
        data-testid={LayoutTestIds.ShowPermissionsButton}
      >
        {t('userProfile.viewRolesAndPermissions')}
      </button>
    </div>
  );

  const notificationsButton = (
    <div>
      <button
        onClick={() => setShowAdditionalSettings(true)}
        className="user-profile-modal-button"
        data-testid={LayoutTestIds.ShowNotificationsButton}
      >
        {t('userProfile.viewNotificationSettings')}
      </button>
    </div>
  );

  // #endregion

  const drawerContent = () => {
    if (isLoading)
      return (
        <div className="user-profile-loader">
          <ABIInlineLoader />
        </div>
      );
    else if (isError)
      return (
        <div className="user-profile-error-content">
          <div className="user-profile-error-message">{t('userProfile.errorMessage')}</div>
          <ABIAppButton onClick={async () => await invalidateSettingsQuery()}>
            {t('common.actions.tryAgain')}
          </ABIAppButton>
        </div>
      );
    else
      return (
        <div>
          {additionalSettings?.listOfPermissions ? permissionsButton : <></>}
          {additionalSettings?.additionalUserProfileContent ? notificationsButton : <></>}
          {brewerySelectionAccordion}
          {languageAccordion}
          {unitsAccordion}
          {showPermissionsInfo && (
            <UserProfileModal onClose={() => setShowPermissionsInfo(false)} dataTestId={LayoutTestIds.PermissionsModal}>
              {additionalSettings?.listOfPermissions && additionalSettings?.appName && (
                <PermissionsDisplay
                  appName={additionalSettings.appName}
                  listOfPermissions={additionalSettings.listOfPermissions}
                />
              )}
            </UserProfileModal>
          )}
          {showAdditionalSettings && (
            <UserProfileModal
              onClose={() => setShowAdditionalSettings(false)}
              dataTestId={LayoutTestIds.NotificationsModal}
            >
              {additionalSettings?.additionalUserProfileContent}
            </UserProfileModal>
          )}
        </div>
      );
  };

  return (
    <ABIActionDrawer
      buttonClassName="user-profile-action-drawer-button"
      buttonContent={userProfileActionDrawerButton}
      className="user-profile-action-drawer"
      closeOnContainerClick={false}
      drawerClassName="user-profile-action-drawer-content"
      drawerContainerPosition={DrawerContainerPosition.BottomRight}
      isDrawerInitiallyOpen={isUserProfileActionDrawerOpen}
      onButtonClick={() => setIsUserProfileActionDrawerOpen(!isUserProfileActionDrawerOpen)}
      data-testid={LayoutTestIds.UserProfileButton}
    >
      {drawerContent()}
    </ABIActionDrawer>
  );
};
